import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, getAccessToken, setAccessToken } from '../api/client';
import type { LoginResponse, User } from '../api/types';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  isDemo: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginDemo: () => Promise<void>;
  register: (data: {
    email: string;
    password: string;
    name: string;
    surname: string;
    representationEntityType: 'fisica' | 'juridica';
    representativeNif: string;
  }) => Promise<string>;
  logout: () => Promise<void>;
  deleteAccount: (password: string) => Promise<string>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    if (!getAccessToken()) {
      setUser(null);
      return;
    }
    try {
      const me = await api.get<User>('/api/account/me');
      setUser(me);
    } catch {
      setAccessToken(null);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    refreshUser().finally(() => setLoading(false));
  }, [refreshUser]);

  const login = useCallback(async (email: string, password: string) => {
    const result = await api.post<LoginResponse>('/api/account/login', { email, password });
    setAccessToken(result.accessToken);
    setUser(result.user);
  }, []);

  const loginDemo = useCallback(async () => {
    const result = await api.post<LoginResponse>('/api/account/demo-login', {});
    setAccessToken(result.accessToken);
    setUser(result.user);
  }, []);

  const register = useCallback(
    async (data: {
      email: string;
      password: string;
      name: string;
      surname: string;
      representationEntityType: 'fisica' | 'juridica';
      representativeNif: string;
    }) => {
      const result = await api.post<{ message: string }>('/api/account/register', data);
      return result.message;
    },
    []
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/api/account/logout');
    } catch {
      // ignore
    } finally {
      setAccessToken(null);
      setUser(null);
    }
  }, []);

  const deleteAccount = useCallback(async (password: string) => {
    const result = await api.post<{ message: string }>('/api/account/delete', { password });
    setAccessToken(null);
    setUser(null);
    return result.message;
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      isDemo: !!user?.isDemo,
      login,
      loginDemo,
      register,
      logout,
      deleteAccount,
      refreshUser,
    }),
    [user, loading, login, loginDemo, register, logout, deleteAccount, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return ctx;
}
